// Duration display normalization for the schedule review grid.
//
// Matches the semantics of the main web app's Bryntum gantt (snagr-web Task
// Viewer): a task's duration is the WORKING time between the range's
// startDate and endDate on the task's calendar (Bryntum's scheduling engine
// computes it from the dates and the calendar; parents are summaries rolled
// up from their children), expressed in the calendar's units — a "day" is
// hoursPerDay working hours, a "month" daysPerMonth days, a "year" 12 months
// — promoted to the largest unit >= 1 and shown to one decimal. That is what
// `formatTaskDuration` renders. Dates are also the only representation that
// survives the backend round-trip unchanged, so preferring them keeps initial
// import and re-import displays identical.
//
// When the calendar can't be resolved or a date is missing we fall back to
// the duration object, which arrives in one of two shapes:
//   - Parser shape: XMLTaskParser emits elapsed 24h-day diffs, e.g.
//     { value: 464.04, unit: 'day', weighting: 3712.32 }.
//   - Backend shape: on GET the API re-humanizes to the largest unit >= 1 with
//     no rounding, e.g. { value: 1.9335, unit: 'year', weighting: 3712.32 }.
// The backend humanizes with the timeRange's calendar factors (hoursPerDay/
// daysPerWeek/daysPerMonth, year = 12 months; fallback 8/5/20 when the
// calendarId doesn't resolve), so the fallback conversion reverses with the
// same factors. `weighting` is never trusted for display: nested GETs
// overwrite PARENT weightings with the sum of child weightings (additive
// work, not the span) while value/unit stay span-derived.

import { calculateWorkingMinutesBetweenMs } from '@/utils/CalendarEndDateCalculator';

// Backend fallback factors (TimeRangeDurationHelper defaults / new Calendar()).
const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_DAYS_PER_WEEK = 5;
const DEFAULT_DAYS_PER_MONTH = 20;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;

// The seed 24/7 default calendar (MMCAL247) every tracking range without a
// mapped calendar is committed against: working time is elapsed time.
const FULL_TIME_FACTORS = { hoursPerDay: 24, daysPerWeek: 7, daysPerMonth: 30 };

const UNIT_LABELS = { minute: 'min', hour: 'hr', day: 'd', week: 'wk', month: 'mo', year: 'yr' };

const round2 = (n) => Math.round(n * 100) / 100;

const resolveFactors = (calendar) => ({
  hoursPerDay: calendar?.hoursPerDay > 0 ? calendar.hoursPerDay : DEFAULT_HOURS_PER_DAY,
  daysPerWeek: calendar?.daysPerWeek > 0 ? calendar.daysPerWeek : DEFAULT_DAYS_PER_WEEK,
  daysPerMonth: calendar?.daysPerMonth > 0 ? calendar.daysPerMonth : DEFAULT_DAYS_PER_MONTH
});

/**
 * Elapsed 24h days between two date-likes — the gantt's duration semantics.
 * @returns {number|null} days, or null when either date is missing/invalid
 *   or the span is not positive.
 */
export function elapsedDays(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const ms = new Date(endDate) - new Date(startDate);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return ms / MS_PER_DAY;
}

/**
 * Fallback: recover the day-equivalent span from a duration object via
 * value+unit — the backend humanizes with these exact per-calendar factors,
 * so this reverses it losslessly. Falls back to `weighting` / hoursPerDay
 * only for an unknown unit, and never for parents' summed weightings (see
 * module comment).
 * @param {object} dur - { value, unit, weighting? }
 * @param {object} [calendar] - { hoursPerDay, daysPerWeek, daysPerMonth }
 * @returns {number|null} days, or null when the duration has no value.
 */
export function durationToDays(dur, calendar) {
  if (!dur || dur.value === null || dur.value === undefined) return null;
  const { hoursPerDay, daysPerWeek, daysPerMonth } = resolveFactors(calendar);
  switch (dur.unit) {
    case 'minute': return dur.value / 60 / hoursPerDay;
    case 'hour': return dur.value / hoursPerDay;
    case 'day': return dur.value;
    case 'week': return dur.value * daysPerWeek;
    case 'month': return dur.value * daysPerMonth;
    case 'year': return dur.value * 12 * daysPerMonth;
  }
  if (typeof dur.weighting === 'number') return dur.weighting / hoursPerDay;
  return dur.value;
}

const formatElapsed = (days) => {
  if (days >= 1) return `${round2(days)}d`;
  const hours = days * 24;
  if (hours >= 1) return `${round2(hours)}hr`;
  return `${Math.round(hours * 60)}min`;
};

/**
 * Human-readable duration for a timeRange. Prefers elapsed start→end days
 * (gantt semantics); falls back to the duration object when a date is
 * missing. Sub-day durations render in their natural small unit.
 * @returns {string} e.g. "464.04d", "4hr", or '' when nothing is known.
 */
export function formatTimeRangeDuration(timeRange, dur, calendar) {
  const elapsed = elapsedDays(timeRange?.startDate, timeRange?.endDate);
  if (elapsed !== null) return formatElapsed(elapsed);
  return formatDurationDisplay(dur, calendar);
}

/**
 * Fallback formatter for a bare duration object (no usable dates).
 * @returns {string} e.g. "464.04d", "4hr", or '' when there is no duration.
 */
export function formatDurationDisplay(dur, calendar) {
  const days = durationToDays(dur, calendar);
  if (days === null) return '';
  if (days >= 1) {
    return `${round2(days)}d`;
  }
  const label = UNIT_LABELS[dur.unit] || 'd';
  return `${round2(dur.value)}${label}`;
}

/**
 * Duration formatted the way the main web app (snagr-web Task Viewer)
 * displays it — the backend's humanized representation: working hours
 * promoted to the largest unit >= 1 (year -> month -> week -> day -> hour ->
 * minute) with the calendar's factors, mirroring TimeRangeDurationHelper.
 * ToCalendarDaysFromDurationWeighting, then rounded to 1 decimal with a
 * pluralized unit name, e.g. "1.5 years", "4.9 months", "3 days".
 *
 * Hours are recovered from value+unit (idempotent for already-humanized
 * backend objects), never from `weighting` — nested GETs overwrite parent
 * weightings with child sums (see module comment).
 * @returns {string} e.g. "1.5 years", or '' when there is no duration.
 */
export function humanizeWorkingDuration(dur, calendar) {
  if (!dur || dur.value === null || dur.value === undefined) return '';
  const factor = hoursPerUnitFor(calendar)[dur.unit];
  if (factor === undefined) return '';
  return humanizeHours(dur.value * factor, calendar);
}

const hoursPerUnitFor = (calendar) => {
  const { hoursPerDay, daysPerWeek, daysPerMonth } = resolveFactors(calendar);
  return {
    minute: 1 / 60,
    hour: 1,
    day: hoursPerDay,
    week: daysPerWeek * hoursPerDay,
    month: daysPerMonth * hoursPerDay,
    year: 12 * daysPerMonth * hoursPerDay
  };
};

/**
 * Working hours -> the backend/SPA humanized string: largest unit >= 1 with
 * the calendar's factors, one decimal, pluralized. e.g. 40h on an 8h/5d
 * calendar -> "1 week".
 */
export function humanizeHours(hours, calendar) {
  if (!Number.isFinite(hours) || hours < 0) return '';
  if (hours === 0) return pluralize(0, 'day');
  const hoursPerUnit = hoursPerUnitFor(calendar);
  const cascade = ['year', 'month', 'week', 'day', 'hour'];
  for (const unit of cascade) {
    if (hours >= hoursPerUnit[unit]) return pluralize(hours / hoursPerUnit[unit], unit);
  }
  return pluralize(hours * 60, 'minute');
}

/**
 * The duration the main web app's gantt shows for a task: the WORKING time
 * between the range's startDate and endDate on its calendar (Bryntum derives
 * it from the dates; parents are summaries of their children), humanized
 * with that calendar's factors.
 *
 * With dates but no usable calendar the span is still the dates' — never the
 * duration object, whose value may be an elapsed-time fallback that would be
 * over-read as working days:
 *  - a resolved calendar with no working intervals: the SPA schedules it as
 *    all-time-working (unspecifiedTimeIsWorking) and keeps its factors;
 *  - no calendar at all: tracking ranges are committed against the seed 24/7
 *    default calendar, so elapsed hours with its 24h/7d/30d factors.
 * Only a range without dates falls back to the duration object, then to the
 * elapsed formatter.
 *
 * Known edge: the SPA takes the unit from the API's humanized object, so a
 * span a few hours short of a "year" renders "11.9 months" here where the
 * SPA may show "1 year" — same quantity, one unit apart.
 * @returns {string} e.g. "6.4 years", "4 days", or '' when nothing is known.
 */
export function formatTaskDuration(timeRange, dur, calendar) {
  const startMs = Date.parse(timeRange?.startDate);
  const endMs = Date.parse(timeRange?.endDate);
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs) {
    if (calendar) {
      const minutes = calculateWorkingMinutesBetweenMs(calendar, startMs, endMs);
      if (minutes !== null) return humanizeHours(minutes / 60, calendar);
      return humanizeHours((endMs - startMs) / MS_PER_HOUR, calendar);
    }
    return humanizeHours((endMs - startMs) / MS_PER_HOUR, FULL_TIME_FACTORS);
  }
  return humanizeWorkingDuration(dur, calendar) || formatTimeRangeDuration(timeRange, dur, calendar);
}

const pluralize = (value, unit) => {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'}`;
};

/**
 * Duration for Bryntum bar rendering, using the same elapsed-first semantics
 * so bar lengths agree with the displayed duration and the range's real
 * end date.
 * @returns {{ value: number|null, unit: string }}
 */
export function toBryntumDuration(timeRange, dur, calendar) {
  const elapsed = elapsedDays(timeRange?.startDate, timeRange?.endDate);
  if (elapsed !== null) {
    if (elapsed >= 1) return { value: round2(elapsed), unit: 'day' };
    const hours = elapsed * 24;
    if (hours >= 1) return { value: round2(hours), unit: 'hour' };
    return { value: Math.round(hours * 60), unit: 'minute' };
  }
  const days = durationToDays(dur, calendar);
  if (days === null) return { value: null, unit: 'day' };
  if (days >= 1) {
    return { value: round2(days), unit: 'day' };
  }
  return { value: dur.value, unit: dur.unit || 'day' };
}
